import { BOM, BOMComponent, BOMFormData, BOMComponentFormData } from '@/types/bom';

export interface BOMBaseState {
  boms: BOM[];
  initialized: boolean;
  isLoading: boolean;
  error: string | null;
  
  // Base BOM operations
  fetchBOMs: () => Promise<void>;
  createBOM: (data: BOMFormData) => Promise<BOM>;
  updateBOM: (id: string, data: Partial<BOMFormData>) => Promise<void>;
  deleteBOM: (id: string) => Promise<void>;
  approveBOM: (id: string) => Promise<void>;
  rejectBOM: (id: string, reason: string) => Promise<void>;
}

export interface BOMComponentState {
  // Basic state
  isLoading: boolean;
  error: string | null;
  
  // Cache to prevent excessive fetching
  componentsCache: Record<string, {
    timestamp: number;
    data: BOMComponent[];
  } | null>;
  
  // Component operations for hierarchical BOM
  fetchBOMComponents: (bomId: string) => Promise<BOMComponent[]>;
  addBOMComponent: (bomId: string, data: BOMComponentFormData) => Promise<BOMComponent>;
  updateBOMComponent: (id: string, data: Partial<BOMComponentFormData>) => Promise<void>;
  deleteBOMComponent: (id: string) => Promise<void>;
  updateComponentSequence: (id: string, newSequence: number) => Promise<boolean>;
  moveBOMComponent: (id: string, newParentId: string | null) => Promise<void>;
  batchAddBOMComponents: (bomId: string, parentComponentId: string | null, components: BOMComponentFormData[]) => Promise<boolean>;
  clearCache: (bomId: string) => void;

  // Utility functions for hierarchical operations
  buildBOMHierarchy: (components: BOMComponent[]) => BOMComponent[];
  getBOMHierarchy: (bomId: string) => Promise<BOMComponent[]>;
}

// Combined BOM state merges both state objects
// Note: isLoading and error properties are shared between both states
export interface BOMState extends Omit<BOMBaseState, 'isLoading' | 'error'>, BOMComponentState {}

// Debug level configuration
export enum LogLevel {
  NONE = 0,
  ERROR = 1,
  WARN = 2,
  INFO = 3,
  DEBUG = 4
}

// Global configuration for BOM store
export const BOMConfig = {
  logLevel: LogLevel.DEBUG, // Changed to DEBUG to see all logs including cache operations
  cacheTimeMs: 1000, // 1 second cache validity
  debounceTimeMs: 1000 // 1 second debounce for real-time updates
};
